import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FileText, Play } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Modal } from '@/components/common/Modal';
import { SelectField, TextField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { useWorkingContext } from '@/hooks/useWorkingContext';
import { gradeOfferingsApi } from '@/services/api/endpoints/academic';
import { invoicesApi } from '@/services/api/endpoints/finance';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { ROUTES } from '@/app/router/routes';
import { PERMISSIONS } from '@/types/permissions';
import type { Invoice, InvoiceStatus } from '@/types/finance';
import type { BillingRunResult } from '@/types/finance';
import { formatDate, formatMoney, orDash } from '@/utils/format';
import { InvoiceStatusBadge, Money } from './shared';

const STATUSES: { value: InvoiceStatus; label: string }[] = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'ISSUED', label: 'Issued' },
  { value: 'PARTIALLY_PAID', label: 'Partially paid' },
  { value: 'PAID', label: 'Paid' },
  { value: 'OVERDUE', label: 'Overdue' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const runSchema = z.object({
  gradeOfferingId: z.string(),
  periodStart: z.string().min(1, 'Choose the month being billed'),
  issueDate: z.string().min(1, 'An issue date is required'),
  issueImmediately: z.boolean(),
});

type RunValues = z.infer<typeof runSchema>;

/**
 * Invoices, and the billing run that raises them.
 *
 * <p>The run offers a dry run first and defaults to it. A billing run is the
 * single most consequential button in the module — it can raise a bill for every
 * child in the school — and seeing what it would do before it does it is worth
 * one extra click every time.
 */
export function InvoicesPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const table = useTableQuery('issueDate,desc');
  const context = useWorkingContext();

  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | ''>('');
  const [outstandingOnly, setOutstandingOnly] = useState(false);
  const [runOpen, setRunOpen] = useState(false);
  const [preview, setPreview] = useState<BillingRunResult | null>(null);

  const params = {
    ...table.params,
    ...(context.academicYearId !== null ? { academicYearId: context.academicYearId } : {}),
    ...(statusFilter !== '' ? { status: statusFilter } : {}),
    ...(outstandingOnly ? { outstandingOnly: true } : {}),
  };

  const query = useQuery({
    queryKey: queryKeys.invoices.list(params),
    queryFn: () => invoicesApi.list(params),
  });

  const offerings = useQuery({
    queryKey: queryKeys.gradeOfferings.list({ picker: true, year: context.academicYearId }),
    queryFn: () =>
      gradeOfferingsApi.list({
        page: 0,
        size: 200,
        status: 'ACTIVE',
        ...(context.academicYearId !== null ? { academicYearId: context.academicYearId } : {}),
      }),
    enabled: runOpen,
  });

  const form = useForm<RunValues>({
    resolver: zodResolver(runSchema),
    defaultValues: { gradeOfferingId: '', periodStart: '', issueDate: '', issueImmediately: false },
  });

  const run = useMutation({
    mutationFn: ({ values, dryRun }: { values: RunValues; dryRun: boolean }) =>
      invoicesApi.billingRun({
        academicYearId: context.academicYearId as string,
        ...(values.gradeOfferingId !== '' ? { gradeOfferingId: values.gradeOfferingId } : {}),
        periodStart: values.periodStart,
        issueDate: values.issueDate,
        issueImmediately: values.issueImmediately,
        dryRun,
      }),
    onSuccess: (result) => {
      if (result.dryRun) {
        setPreview(result);
        return;
      }
      toast.success(
        `Raised ${result.invoicesCreated} invoice${result.invoicesCreated === 1 ? '' : 's'}`,
      );
      setPreview(null);
      setRunOpen(false);
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all });
    },
    onError: (error) => handleApiError(error, form.setError),
  });

  const columns: Column<Invoice>[] = [
    {
      key: 'number',
      header: 'Invoice',
      sortKey: 'invoiceNumber',
      render: (invoice) => (
        <div>
          <span className="font-mono text-xs font-medium text-ink">{invoice.invoiceNumber}</span>
          <span className="block text-[11px] text-subtle">{orDash(invoice.billingPeriod)}</span>
        </div>
      ),
    },
    {
      key: 'student',
      header: 'Student',
      render: (invoice) => (
        <div>
          <span className="text-ink">{orDash(invoice.studentName)}</span>
          <span className="block text-[11px] text-muted">
            {orDash(invoice.gradeName)}
            {invoice.sectionName !== undefined ? ` · ${invoice.sectionName}` : ''}
          </span>
        </div>
      ),
    },
    {
      key: 'due',
      header: 'Due',
      sortKey: 'dueDate',
      render: (invoice) => (
        <div className="whitespace-nowrap">
          <span className="tabular-nums text-ink">{formatDate(invoice.dueDate)}</span>
          {invoice.daysOverdue > 0 && (
            <span className="block text-[11px] text-danger">
              {invoice.daysOverdue} day{invoice.daysOverdue === 1 ? '' : 's'} overdue
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'total',
      header: 'Total',
      sortKey: 'totalAmount',
      className: 'text-right',
      render: (invoice) => <Money amount={invoice.totalAmount} />,
    },
    {
      key: 'paid',
      header: 'Paid',
      className: 'text-right',
      render: (invoice) => <Money amount={invoice.amountPaid} />,
    },
    {
      key: 'outstanding',
      header: 'Outstanding',
      sortKey: 'outstandingAmount',
      className: 'text-right',
      render: (invoice) => <Money amount={invoice.outstandingAmount} bold />,
    },
    {
      key: 'status',
      header: 'Status',
      sortKey: 'status',
      render: (invoice) => <InvoiceStatusBadge status={invoice.status} />,
    },
  ];

  return (
    <>
      <ListPage
        title="Invoices"
        description="Bills raised against the working academic year. Amounts are shown in the school's currency."
        icon={<FileText className="h-5 w-5" aria-hidden="true" />}
        action={
          can(PERMISSIONS.INVOICE_CREATE) ? (
            <Button onClick={() => { setPreview(null); setRunOpen(true); }}>
              <Play className="h-4 w-4" aria-hidden="true" />
              Billing run
            </Button>
          ) : undefined
        }
        search={{
          value: table.searchInput,
          onChange: table.setSearchInput,
          placeholder: 'Search by invoice number…',
        }}
        filters={
          <>
            <FilterSelect
              label="Status"
              value={statusFilter}
              onChange={(value) => { setStatusFilter(value); table.setPage(0); }}
              options={STATUSES}
            />
            <label className="flex items-center gap-2 text-sm text-muted">
              <input
                type="checkbox"
                checked={outstandingOnly}
                onChange={(event) => { setOutstandingOnly(event.target.checked); table.setPage(0); }}
                className="h-4 w-4 border-line"
              />
              Owing money only
            </label>
          </>
        }
        pagination={query.data?.pagination}
        onPageChange={table.setPage}
        onSizeChange={table.setSize}
      >
        <DataTable
          columns={columns}
          rows={query.data?.data}
          rowKey={(invoice) => invoice.id}
          loading={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          onRowClick={(invoice) => navigate(ROUTES.invoiceDetail(invoice.id))}
          emptyTitle="No invoices yet"
          emptyDescription="Run the billing for a period to raise the instalments that are due."
          emptyIcon={<FileText className="h-6 w-6" aria-hidden="true" />}
        />
      </ListPage>

      <Modal
        open={runOpen}
        size="lg"
        title="Billing run"
        description="Raises one invoice per student for the period, listing every fee due."
        onClose={() => { setRunOpen(false); setPreview(null); }}
        footer={
          <>
            <Button variant="secondary" onClick={() => { setRunOpen(false); setPreview(null); }}>
              Cancel
            </Button>
            <Button
              variant="secondary"
              loading={run.isPending && run.variables?.dryRun === true}
              onClick={form.handleSubmit((values) => run.mutate({ values, dryRun: true }))}
            >
              Preview
            </Button>
            <Button
              disabled={preview === null}
              loading={run.isPending && run.variables?.dryRun === false}
              onClick={form.handleSubmit((values) => run.mutate({ values, dryRun: false }))}
            >
              {preview === null ? 'Preview first' : `Raise ${preview.invoicesCreated} invoices`}
            </Button>
          </>
        }
      >
        <form className="space-y-4" noValidate>
          <SelectField
            label="Class"
            hint="Leave blank to bill every class in the year."
            {...form.register('gradeOfferingId')}
          >
            <option value="">Every class</option>
            {(offerings.data?.data ?? []).map((offering) => (
              <option key={offering.id} value={offering.id}>
                {offering.grade.name} · {offering.campusName ?? 'All campuses'}
              </option>
            ))}
          </SelectField>

          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Period being billed"
              type="date"
              required
              hint="The first day of the month. October's tuition is the 1st, whenever you run it."
              error={form.formState.errors.periodStart?.message}
              {...form.register('periodStart')}
            />
            <TextField
              label="Issue date"
              type="date"
              required
              hint="Due dates come from the price list's own rule."
              error={form.formState.errors.issueDate?.message}
              {...form.register('issueDate')}
            />
          </div>

          <label className="flex items-start gap-2.5 border border-line bg-surface-2 px-3.5 py-3">
            <input type="checkbox" className="mt-0.5 h-4 w-4 border-line"
                   {...form.register('issueImmediately')} />
            <span>
              <span className="block text-[13px] font-medium text-ink">Issue immediately</span>
              <span className="block text-xs text-muted">
                Leaving this off raises drafts you can check first. An issued invoice's charges
                are frozen and the family owes the money.
              </span>
            </span>
          </label>

          {preview !== null && (
            <div className="border-2 border-line bg-surface px-4 py-3.5">
              <strong className="text-[13px] font-extrabold text-ink">
                Preview — nothing has been written
              </strong>
              <dl className="mt-2.5 grid grid-cols-2 gap-x-6 gap-y-1.5 text-[13px] sm:grid-cols-4">
                <div><dt className="text-subtle">Invoices</dt>
                     <dd className="tabular-nums text-ink">{preview.invoicesCreated}</dd></div>
                <div><dt className="text-subtle">Lines</dt>
                     <dd className="tabular-nums text-ink">{preview.linesCreated}</dd></div>
                <div><dt className="text-subtle">Already billed</dt>
                     <dd className="tabular-nums text-ink">{preview.alreadyBilled}</dd></div>
                <div><dt className="text-subtle">Total</dt>
                     <dd className="tabular-nums text-ink">{formatMoney(preview.totalBilled)}</dd></div>
              </dl>
              {preview.warnings.length > 0 && (
                <ul className="mt-2.5 space-y-1">
                  {preview.warnings.map((warning) => (
                    <li key={warning} className="text-xs text-warning">{warning}</li>
                  ))}
                </ul>
              )}
              {preview.alreadyBilled > 0 && (
                <p className="mt-2 text-xs text-muted">
                  Instalments already billed for this period are skipped — running again cannot
                  raise them twice.
                </p>
              )}
            </div>
          )}
        </form>
      </Modal>
    </>
  );
}
